import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiMessageService } from "@/iti-core-react/services/iti-message-service";
import BaseForm from "@/pages/Base/BaseForm/BaseForm";
import CardForm from "@/shared/components/CardForm/CardForm";
import type { ReportDto } from "@/shared/dtos/Support/report-dto";
import { SupportService } from "@/shared/services/supportService";
import { useState } from "react";

import ItiPopUpComponent from "@/iti-core-react/components/ItiPopUp/ItiPopUp";
import { useDevice } from "@/utils/media-query";
import { SupportFormFields } from "./components/SupportFormFields";
import "./SupportPopup.scss";

type SupportContext = "catalogo" | "procesos_ingesta" | "glosarios_negocio";

interface SupportFormState {
	subject: string;
	description: string;
	context: SupportContext | "";
	screenshots: File[];
	isSubmitting: boolean;
	showValidation: boolean;
}

interface SupportPopupProps {
	readonly visible: boolean;
	readonly onHide: () => void;
}

export default function SupportPopup({ visible, onHide }: SupportPopupProps) {
	const { t } = useLanguage();
	const { isDesktop } = useDevice();

	const [formState, setFormState] = useState<SupportFormState>({
		subject: "",
		description: "",
		context: "",
		screenshots: [],
		isSubmitting: false,
		showValidation: false,
	});

	const validateForm = (): boolean => {
		return !!(
			formState.subject.trim().length >= 5 &&
			formState.subject.trim().length <= 140 &&
			formState.description.trim().length >= 10 &&
			formState.context
		);
	};

	const updateSubject = (subject: string): void => {
		setFormState((prev) => ({ ...prev, subject }));
	};
	const updateDescription = (description: string): void => {
		setFormState((prev) => ({ ...prev, description }));
	};
	const updateContext = (context: SupportContext | ""): void => {
		setFormState((prev) => ({ ...prev, context }));
	};
	const updateScreenshots = (screenshots: File[]): void => {
		setFormState((prev) => ({ ...prev, screenshots }));
	};

	const handleSubmit = async (): Promise<void> => {
		setFormState((prev) => ({ ...prev, showValidation: true }));

		if (!validateForm()) {
			ItiMessageService.toastError(t("support.messages.validationError"));
			return;
		}

		setFormState((prev) => ({ ...prev, isSubmitting: true }));

		const reportData: ReportDto = {
			subject: formState.subject.trim(),
			description: formState.description.trim(),
			context: formState.context,
			files:
				formState.screenshots.length > 0 ? formState.screenshots : undefined,
		};

		const response = await SupportService.createUserReport(reportData);

		const formattedDate = new Date(response.createdDate).toLocaleString(
			"es-ES",
			{
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			},
		);
		ItiMessageService.toastSuccess(
			`<i class="mdi mdi-check-circle-outline mdi-18px" aria-hidden="true"></i> ${t(
				"support.messages.submitSuccess",
			)}<br/><br/>` +
				`<i class="mdi mdi-clipboard-text-outline mdi-18px" aria-hidden="true"></i> ${t(
					"support.messages.reportIdInfo",
				)}: ${response.reportId}<br/>` +
				`<i class="mdi mdi-calendar-clock-outline mdi-18px" aria-hidden="true"></i> ${t(
					"support.messages.createdDate",
				)}: ${formattedDate}`,
			undefined,
			undefined,
			15000,
			true,
			true,
		);

		setFormState({
			subject: "",
			description: "",
			context: "",
			screenshots: [],
			isSubmitting: false,
			showValidation: false,
		});

		onHide();

		setFormState((prev) => ({ ...prev, isSubmitting: false }));
	};

	const handleCancel = (): void => {
		onHide();
	};

	return (
		<ItiPopUpComponent
			title={`${t("support.title")}`}
			visible={visible}
			titleIcon="mdi mdi-face-agent icon-title-dataset support-icon mx-4 "
			cssTitle="class-title"
			width={isDesktop ? "80%" : "90%"}
			maxWidth={1260}
			height="auto"
			maxHeight={"90vh"}
			onHide={onHide}
		>
			<BaseForm
				showSubmit={true}
				showCancel={true}
				card={false}
				buttonText={t("support.actions.submit")}
				onSave={handleSubmit}
				onCancel={handleCancel}
			>
				<div className="row">
					<div className="col-12">
						<CardForm title={t("support.subtitle")} icon="mdi mdi-bug-outline">
							<SupportFormFields
								formState={formState}
								updateSubject={updateSubject}
								updateDescription={updateDescription}
								updateContext={updateContext}
								updateScreenshots={updateScreenshots}
							/>
						</CardForm>
					</div>
				</div>
			</BaseForm>
		</ItiPopUpComponent>
	);
}
